// packages/shared/src/utils/visitAlerts.ts
// Visit-alert thresholds (org setting `settings.visit_alerts`) and the severity
// readers shared by the patient chart (prescribed visits, benefit limits,
// scheduled-ahead), CasePrescriptionPanel, and the reporting tab.
//
// settings.visit_alerts = { remaining_amber, remaining_red, min_upcoming }
//   remaining_amber: remaining visits at or below this → amber (default 5)
//   remaining_red:   remaining visits at or below this → red   (default 2)
//   min_upcoming:    fewer upcoming appointments than this → amber (default 3)
// Missing/blank keys fall back to the defaults; every value is an int >= 1.

export interface VisitAlertThresholds {
  remaining_amber: number;
  remaining_red: number;
  min_upcoming: number;
}

export type RemainingSeverity = 'red' | 'amber' | null;
export type UpcomingSeverity = 'amber' | null;

export const DEFAULT_VISIT_ALERT_THRESHOLDS: VisitAlertThresholds = {
  remaining_amber: 5,
  remaining_red: 2,
  min_upcoming: 3,
};

function toThreshold(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : fallback;
}

/**
 * Merge `orgSettings.visit_alerts` over the defaults. Accepts the whole
 * organization settings object (or null/undefined).
 */
export function getVisitAlertThresholds(orgSettings: any): VisitAlertThresholds {
  const raw = (orgSettings && typeof orgSettings === 'object' && orgSettings.visit_alerts) || {};
  return {
    remaining_amber: toThreshold(raw.remaining_amber, DEFAULT_VISIT_ALERT_THRESHOLDS.remaining_amber),
    remaining_red: toThreshold(raw.remaining_red, DEFAULT_VISIT_ALERT_THRESHOLDS.remaining_red),
    min_upcoming: toThreshold(raw.min_upcoming, DEFAULT_VISIT_ALERT_THRESHOLDS.min_upcoming),
  };
}

/**
 * Severity for a remaining-visit count (prescribed or benefit-limit).
 * `remaining <= remaining_red` → 'red', `<= remaining_amber` → 'amber', else null.
 * Non-numeric input → null.
 */
export function remainingSeverity(
  remaining: number | null | undefined,
  thresholds: VisitAlertThresholds = DEFAULT_VISIT_ALERT_THRESHOLDS,
): RemainingSeverity {
  if (remaining == null || typeof remaining !== 'number' || !Number.isFinite(remaining)) return null;
  if (remaining <= thresholds.remaining_red) return 'red';
  if (remaining <= thresholds.remaining_amber) return 'amber';
  return null;
}

/**
 * Severity for the number of upcoming scheduled appointments.
 * `count < min_upcoming` → 'amber' (0 included), else null.
 */
export function upcomingSeverity(
  count: number | null | undefined,
  thresholds: VisitAlertThresholds = DEFAULT_VISIT_ALERT_THRESHOLDS,
): UpcomingSeverity {
  if (count == null || typeof count !== 'number' || !Number.isFinite(count)) return null;
  return count < thresholds.min_upcoming ? 'amber' : null;
}
